/**
 * LessonPlanBuilderModal — the "Generate course material" wand button
 * lives in LMSAdmin lesson forms and opens this modal.
 *
 * Inputs: topic, target tier, optional module title, optional existing
 * outline, optional extra notes.
 * Output: a structured lesson plan the admin can preview and then
 * insert into the current lesson's content editor via `onInsert(plan)`.
 *
 * We keep this dumb-and-portable: no coupling to a specific lesson form —
 * the parent hands us `onInsert(markdown)` and we hand back a stitched
 * markdown draft that the parent drops into whatever textarea/RichText
 * it owns.
 */
import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Wand2, Sparkles, CheckCircle2, Info } from 'lucide-react';

const TIER_LABEL = { free: 'Free', pro: 'Pro', business: 'Business', enterprise: 'Enterprise' };

/**
 * Fold the structured plan into a single markdown block suitable for the
 * RichText editor. Sections become H2 headings, key points a bullet list,
 * quiz items an ordered list at the end (admins can copy them into the
 * quiz builder separately).
 *
 * `opts.includeQuiz` (default true) — set false when the caller is
 * wiring the quiz into a real Quiz content section and doesn't want a
 * duplicate copy pasted into the description too.
 */
export function planToMarkdown(plan, opts = {}) {
  if (!plan) return '';
  const { includeQuiz = true } = opts;
  const out = [];
  if (plan.title) out.push(`# ${plan.title}`);
  if (plan.summary) out.push(`\n${plan.summary}\n`);
  if (plan.objectives?.length) {
    out.push('## Learning objectives');
    plan.objectives.forEach((o) => out.push(`- ${o}`));
  }
  (plan.sections || []).forEach((s) => {
    out.push(`\n## ${s.heading}`);
    if (s.time_minutes) out.push(`_~${s.time_minutes} min_`);
    if (s.content) out.push(`\n${s.content}`);
    if (s.key_points?.length) {
      out.push('\n**Key points:**');
      s.key_points.forEach((k) => out.push(`- ${k}`));
    }
  });
  if (plan.resources?.length) {
    out.push('\n## Resources');
    plan.resources.forEach((r) => out.push(`- ${r}`));
  }
  if (includeQuiz && plan.quiz?.length) {
    out.push('\n## Quick check');
    plan.quiz.forEach((q, i) => {
      out.push(`\n**Q${i + 1}. ${q.question}**`);
      (q.options || []).forEach((opt, oi) => {
        const mark = oi === q.answer_index ? ' ✅' : '';
        out.push(`- ${opt}${mark}`);
      });
      if (q.explanation) out.push(`_${q.explanation}_`);
    });
  }
  return out.join('\n');
}

export default function LessonPlanBuilderModal({
  open, onOpenChange, onInsert, initialTopic = '', initialTier = 'pro',
  moduleTitle = '',
}) {
  const { API } = useContext(AuthContext);
  const [topic,   setTopic]   = useState(initialTopic);
  const [tier,    setTier]    = useState(initialTier);
  const [outline, setOutline] = useState('');
  const [notes,   setNotes]   = useState('');
  const [loading, setLoading] = useState(false);
  const [plan,    setPlan]    = useState(null);
  // Live-preview of the Standards guardrails for the selected tier.
  // Fetched from the admin standards endpoint (admins have access) so the
  // preview is always in sync with whatever they saved on the Standards tab.
  const [tierStandard, setTierStandard] = useState(null);
  const [tierStandardLoading, setTierStandardLoading] = useState(false);

  // Re-fetch when the target tier changes (and once on open).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTierStandardLoading(true);
    axios.get(`${API}/admin/education-standards/${tier}`)
      .then((res) => { if (!cancelled) setTierStandard(res.data); })
      .catch(() => { if (!cancelled) setTierStandard(null); })
      .finally(() => { if (!cancelled) setTierStandardLoading(false); });
    return () => { cancelled = true; };
  }, [tier, open, API]);

  const reset = () => {
    setPlan(null);
    setLoading(false);
  };

  const generate = async () => {
    if (!topic.trim() || topic.trim().length < 3) {
      toast.error('Please enter a topic (at least 3 characters).');
      return;
    }
    setLoading(true);
    setPlan(null);
    try {
      const res = await axios.post(`${API}/admin/ai/lesson-plan-builder`, {
        topic:            topic.trim(),
        target_tier:      tier,
        module_title:     moduleTitle || undefined,
        existing_outline: outline.trim() || undefined,
        extra_notes:      notes.trim() || undefined,
      });
      setPlan(res.data);
      toast.success('Lesson plan generated');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Generation failed';
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const doInsert = () => {
    if (!plan) return;
    const md = planToMarkdown(plan);
    onInsert?.(md, plan);
    onOpenChange?.(false);
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange?.(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lesson-plan-builder-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-amber-500" />
            Generate course material
          </DialogTitle>
          <DialogDescription>
            Draft a lesson grounded in your Knowledge Base + the target tier&apos;s Standards of Education.
            Review, then insert into the lesson editor.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div>
            <Label htmlFor="lp-topic">Topic</Label>
            <Input
              id="lp-topic"
              placeholder="e.g. Troubleshooting a collapsed strawberry batch"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              data-testid="lesson-plan-topic-input"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Label>Target tier</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      title="Preview the guardrails applied to this tier"
                      data-testid="lesson-plan-standards-preview-trigger"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-96 max-h-[60vh] overflow-y-auto text-xs"
                    data-testid="lesson-plan-standards-preview-popover"
                  >
                    {tierStandardLoading ? (
                      <p className="text-slate-500">Loading standard…</p>
                    ) : !tierStandard ? (
                      <p className="text-slate-500">
                        No standard found for this tier — factory defaults will apply.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <p className="font-semibold text-slate-800 dark:text-slate-100">
                          {TIER_LABEL[tierStandard.tier] || tierStandard.tier} — Standards preview
                        </p>
                        {tierStandard.tone_guidance ? (
                          <div>
                            <p className="uppercase tracking-wide font-semibold text-slate-500 text-[10px]">Tone</p>
                            <p className="whitespace-pre-wrap">{tierStandard.tone_guidance}</p>
                          </div>
                        ) : null}
                        {tierStandard.depth_requirements ? (
                          <div>
                            <p className="uppercase tracking-wide font-semibold text-slate-500 text-[10px]">Depth</p>
                            <p className="whitespace-pre-wrap">{tierStandard.depth_requirements}</p>
                          </div>
                        ) : null}
                        {tierStandard.learning_objectives?.length ? (
                          <div>
                            <p className="uppercase tracking-wide font-semibold text-slate-500 text-[10px]">Objectives</p>
                            <ul className="list-disc pl-4 space-y-0.5">
                              {tierStandard.learning_objectives.map((o, i) => <li key={i}>{o}</li>)}
                            </ul>
                          </div>
                        ) : null}
                        {tierStandard.content_do?.length ? (
                          <div>
                            <p className="uppercase tracking-wide font-semibold text-emerald-600 text-[10px]">DO</p>
                            <ul className="list-disc pl-4 space-y-0.5">
                              {tierStandard.content_do.map((x, i) => <li key={i}>{x}</li>)}
                            </ul>
                          </div>
                        ) : null}
                        {tierStandard.content_dont?.length ? (
                          <div>
                            <p className="uppercase tracking-wide font-semibold text-rose-600 text-[10px]">DON&apos;T</p>
                            <ul className="list-disc pl-4 space-y-0.5">
                              {tierStandard.content_dont.map((x, i) => <li key={i}>{x}</li>)}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger data-testid="lesson-plan-tier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIER_LABEL).map(([v, l]) => (
                    <SelectItem key={v} value={v} data-testid={`lesson-plan-tier-option-${v}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tierStandard?.tone_guidance ? (
                <p
                  className="text-[11px] text-slate-500 mt-1 line-clamp-2"
                  data-testid="lesson-plan-standards-summary"
                  title={tierStandard.tone_guidance}
                >
                  <span className="font-semibold">Tone: </span>{tierStandard.tone_guidance}
                </p>
              ) : null}
            </div>
            {moduleTitle ? (
              <div>
                <Label>Parent module</Label>
                <Input value={moduleTitle} disabled />
              </div>
            ) : null}
          </div>

          <div>
            <Label htmlFor="lp-outline">Existing outline (optional)</Label>
            <Textarea
              id="lp-outline"
              rows={3}
              placeholder="Paste any rough outline you already have. The AI will expand/refine it."
              value={outline}
              onChange={(e) => setOutline(e.target.value)}
              data-testid="lesson-plan-outline-input"
            />
          </div>
          <div>
            <Label htmlFor="lp-notes">Extra notes (optional)</Label>
            <Textarea
              id="lp-notes"
              rows={2}
              placeholder="Anything specific to emphasize or avoid."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="lesson-plan-notes-input"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              onClick={generate}
              disabled={loading}
              data-testid="lesson-plan-generate-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {loading ? 'Generating…' : plan ? 'Regenerate' : 'Generate plan'}
            </Button>
          </div>

          {/* Preview */}
          {plan && (
            <div
              className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-800 space-y-3"
              data-testid="lesson-plan-preview"
            >
              <div>
                <h3 className="text-lg font-semibold">{plan.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{plan.summary}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Tier: {TIER_LABEL[plan.tier_applied] || plan.tier_applied}
                  {plan.citations?.length ? ` · ${plan.citations.length} KB citation${plan.citations.length === 1 ? '' : 's'}` : ''}
                </p>
              </div>

              {plan.objectives?.length ? (
                <div>
                  <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">Objectives</p>
                  <ul className="text-sm list-disc pl-5 mt-1">
                    {plan.objectives.map((o, i) => <li key={i}>{o}</li>)}
                  </ul>
                </div>
              ) : null}

              {plan.sections?.length ? (
                <div className="space-y-3">
                  <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">Sections</p>
                  {plan.sections.map((s, i) => (
                    <div key={i} className="border-l-2 border-amber-400 pl-3">
                      <p className="font-medium text-sm">{s.heading}{s.time_minutes ? ` · ~${s.time_minutes} min` : ''}</p>
                      <p className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap mt-1">
                        {s.content}
                      </p>
                      {s.key_points?.length ? (
                        <ul className="text-xs list-disc pl-5 mt-1 text-slate-500">
                          {s.key_points.map((k, ki) => <li key={ki}>{k}</li>)}
                        </ul>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              {plan.quiz?.length ? (
                <div>
                  <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">Quick check</p>
                  <ol className="text-sm list-decimal pl-5 mt-1 space-y-1">
                    {plan.quiz.map((q, i) => (
                      <li key={i}>
                        {q.question}
                        <ul className="list-disc pl-5">
                          {(q.options || []).map((o, oi) => (
                            <li key={oi} className={oi === q.answer_index ? 'font-semibold text-emerald-600 dark:text-emerald-400' : ''}>
                              {o}{oi === q.answer_index ? ' ✓' : ''}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>
          <Button
            onClick={doInsert}
            disabled={!plan}
            data-testid="lesson-plan-insert-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Insert into lesson
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
